import { useCallback } from 'react'
import { useSnackbar } from '@/contexts/SnackBarContext'
import log from '@/providers/log'

type IErrorResponse = { error: { message: string } }

interface IUseOptimisticUpdateParams<T, TAttributes> {
  currentState: T
  setState: (state: T) => void
  updateFn: (attributes: TAttributes) => Promise<T | IErrorResponse>
  onSuccess?: (response: T) => void
  errorMessage?: string
  setServerResponseOnSuccess?: boolean
}
function hasPayload(attrs: object | null | undefined): boolean {
  return typeof attrs === 'object' && attrs !== null && Object.keys(attrs).length > 0
}

function handleUpdateError<T>(opts: {
  previousState: T
  setState: (s: T) => void
  showSnackbar: (msg: string, type: 'error') => void
  errorMessage: string
  detail: string
}) {
  const { previousState, setState, showSnackbar, errorMessage, detail } = opts
  setState(previousState)
  showSnackbar(errorMessage, 'error')
  log.error(`${errorMessage}: ${detail}`)
}

/**
 * Custom hook that provides an optimistic update handler.
 * Immediately updates the UI, then syncs with the server and rolls back on error.
 */
export function useOptimisticUpdate<T, TAttributes extends Partial<T>>({
  currentState,
  setState,
  updateFn,
  onSuccess,
  errorMessage = 'Failed to update',
  setServerResponseOnSuccess = true,
}: IUseOptimisticUpdateParams<T, TAttributes>) {
  const showSnackbar = useSnackbar()

  return useCallback(
    async (attributes: TAttributes) => {
      if (!hasPayload(attributes)) {
        log.warn('useOptimisticUpdate: skipped update with no attributes')
        return
      }
      const previousState = { ...currentState }
      setState({ ...currentState, ...attributes })
      try {
        const response = (await updateFn(attributes)) as IErrorResponse
        if ('error' in response) {
          handleUpdateError({
            previousState,
            setState,
            showSnackbar,
            errorMessage,
            detail: response.error.message,
          })
          return
        }
        if (setServerResponseOnSuccess) {
          setState(response as T)
        }
        onSuccess?.(response as T)
      } catch (error) {
        handleUpdateError({
          previousState,
          setState,
          showSnackbar,
          errorMessage,
          detail: error instanceof Error ? error.message : String(error),
        })
      }
    },
    [currentState, setState, updateFn, onSuccess, errorMessage, showSnackbar, setServerResponseOnSuccess],
  )
}
